import { useCallback, useState } from 'react';
import handleInputChange from '../function/HandleInputsChange';
import AlternativeResult from './AlternativeResult';
import '../css/Result.css';
import DownloadSummaryPdf from '../function/DownloadSummaryPdf';
import HandleSave from '../function/HandleSave';
import { useNotification } from '../components/NotificationContext';
import { useNavigation } from '../function/navigation';
import { handleRedirect } from '../function/handleRedirect';
import { ToBaseUnit, UnitConversion } from '../inputs/UnitConversion';

function Result({
  result,
  inputs,
  setInputs,
  options,
  setOptions,
  saveReady,
  clickTriggeredRef,
  setLaunchId,
  isFetchingData,
}) {
  const { dispatch } = useNavigation();
  const [summaryMode, setSummaryMode] = useState('collapse');
  const [isLoading, setIsLoading] = useState({ basic: false, drawing: false });
  const [generateDrawing, setGenerateDrawing] = useState(false);
  const [priceExpanded, setPriceExpanded] = useState(true);

  const drawingSupported = ['SV', 'SV-L', 'SVs'].includes(inputs.sv_family);
  const notDrawingMachine = !drawingSupported;

  const round = (value, decimal = 2) =>
    (value ?? value === 0) ? +value.toFixed(decimal) : '';

  const currencyMap = {
    1: '€',
    2: '$',
    3: '¥',
    4: '€',
  };
  const currencySymbol = currencyMap[inputs.region] ?? '€';

  const roundedPrice = Math.round(result.price / 100) * 100;
  const priceValue = isNaN(roundedPrice)
    ? inputs.sv_family === 'SV-L'
      ? 'Price not available in this region.'
      : 'Insufficient permissions to view prices. Please contact our sales team at inquiry@ambaflex.com.'
    : `${currencySymbol} ${roundedPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  const isa = inputs.pdp
    ?.filter((p) => p.selected)
    .sort((a, b) => b.isa - a.isa || a.max_incline - b.max_incline);
  const isaValue = isa?.[0]?.isa * result.slope_angle_center;

  const { showNotification, hideNotification } = useNotification();

  const handleSaveAction = (drawing) => {
    HandleSave({
      drawing,
      inputs,
      options,
      result,
      setLaunchId,
      setIsLoading,
      showNotification,
      hideNotification,
      priceValue,
      machineSummary,
    });
  };

  const handleDownloadPdf = () => {
    DownloadSummaryPdf({
      inputs,
      result,
      machineSummary,
      priceValue,
      options,
    });
  };

  const setCurrentPage = useCallback(
    (page) => dispatch({ type: 'currentPage', payload: page }),
    [dispatch]
  );

  const setProductTab = useCallback(
    (tab) => dispatch({ type: 'productCurrentPage', payload: { tab: tab } }),
    [dispatch]
  );

  const { displayedValue: outerDiamConverted, displayedUnit: outerDiamUnit } =
    UnitConversion(result.outside_diameter, inputs.unit_system, 'mm');

  const machineSummary = [
    { label: 'Machine Code', value: result.machine_code },
    {
      label: 'Outer diameter',
      value: `${round(outerDiamConverted, 0) || ''} ${outerDiamUnit}`,
    },
    {
      label: 'In-Out configuration',
      value: (
        <img
          src={`${process.env.PUBLIC_URL}/pics/configuration/config${inputs.configuration}.png`}
          alt={`Configuration ${inputs.configuration}`}
          className="option-image"
        />
      ),
      click: 'configuration',
    },
    {
      label: 'Transport Direction',
      value: inputs.transport_direction,
      click: 'transport_direction',
    },
  ];

  const priceDetail = [
    {
      icon: 'windings',
      alt: 'winding-icon',
      value: round(result?.nbr_windings),
      label: `${round(result?.nbr_windings)} windings`,
    },
    {
      icon: 'angle',
      alt: 'angle-icon',
      value: `${round(result?.slope_angle_center, 1)} °`,
      label: `Centerline slope angle: ${round(result?.slope_angle_center, 1)} °`,
    },
    {
      icon: 'incline',
      alt: 'incline-icon',
      value: `${round(isaValue, 1)} °`,
      label: `Product stability angle (ISA): ${round(isaValue, 1)} °`,
    },
  ];

  return (
    <div className="summary-column">
      <div className={`summary-content ${summaryMode}`}>
        <img
          src={`${process.env.PUBLIC_URL}/icons/${summaryMode === 'collapse' ? 'expand' : 'reduce'}.png`}
          alt="expand-icon"
          className={`expand-icon ${summaryMode}`}
          onClick={() =>
            setSummaryMode(summaryMode === 'collapse' ? 'expand' : 'collapse')
          }
        />
        {summaryMode !== 'collapse' && (
          <div className="summary">
            <h2>Summary</h2>
            <ul>
              {machineSummary.map((item, i) => (
                <li
                  key={i}
                  className={
                    item.label === 'Machine Code' ? 'machine-code-row' : ''
                  }
                >
                  <div className="result-name">{item.label}</div>
                  <div
                    className={`result-value ${item.click ? 'clickable' : ''}`}
                    {...(item.click && {
                      onClick: () =>
                        handleRedirect(
                          item.click,
                          dispatch,
                          setInputs,
                          setProductTab,
                          setCurrentPage,
                          clickTriggeredRef
                        ),
                    })}
                  >
                    {item.value}
                  </div>
                </li>
              ))}
              <li className="last">
                <div className={`result-name`}>Cubic space available</div>
                <RenderCubicSpace
                  inputs={inputs}
                  setInputs={setInputs}
                  setOptions={setOptions}
                  result={result}
                />
              </li>
            </ul>
          </div>
        )}
        <div className={`price ${summaryMode}`}>
          <div className="price-main">
            <h3>Machine price</h3>
            <h3 className={`${isNaN(roundedPrice) ? 'no-permission' : ''}`}>
              {priceValue}
            </h3>
            {summaryMode !== 'collapse' && (
              <div
                className="arrow-container-menu"
                onClick={() => setPriceExpanded(!priceExpanded)}
              >
                <div className={`arrow ${priceExpanded ? 'up' : ''}`}></div>
              </div>
            )}
          </div>
          {summaryMode !== 'collapse' && (
            <div
              className="price-detail"
              onClick={() => setPriceExpanded(!priceExpanded)}
            >
              {priceDetail.map((item, i) => (
                <div key={i} className="price-item">
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/${item.icon}.png`}
                    alt={item.alt}
                    className="price-detail-icon"
                  />
                  {item.value}
                  <span className="price-label">{item.label}</span>
                </div>
              ))}
            </div>
          )}
          {priceExpanded && summaryMode !== 'collapse' && (
            <AlternativeResult
              inputs={inputs}
              setInputs={setInputs}
              options={options}
              setOptions={setOptions}
              result={result}
            />
          )}
        </div>
        <div className={`save ${summaryMode}`}>
          <div className="save-container">
            <label className="switch">
              <input
                type="checkbox"
                checked={generateDrawing}
                onChange={(e) => setGenerateDrawing(e.target.checked)}
                disabled={notDrawingMachine}
              />
              <span className="slider round"></span>
            </label>
            <span className="switch-label">
              {notDrawingMachine
                ? `Drawing for ${inputs.sv_family} not included yet`
                : 'Generate Drawing'}
            </span>
            <button
              id="save-config-button"
              className={`button-md button-primary ${isFetchingData ? 'fetching' : ''}`}
              onClick={() => handleSaveAction(generateDrawing)}
              disabled={
                isFetchingData ||
                isLoading.basic ||
                isLoading.drawing ||
                (generateDrawing && (!saveReady || notDrawingMachine))
              }
            >
              {isLoading.basic || isLoading.drawing ? (
                <>
                  Saving
                  <span className="dotted-spinner" aria-hidden="true"></span>
                </>
              ) : generateDrawing ? (
                'Save + Drawing'
              ) : (
                'Save'
              )}
            </button>
          </div>
          <div className="pdf-container">
            <img
              src={`${process.env.PUBLIC_URL}/icons/file-pdf.png`}
              alt="file-pdf"
              className="save-pdf"
              onClick={handleDownloadPdf}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const RenderCubicSpace = ({ inputs, setInputs, setOptions, result }) => {
  const [editMode, setEditMode] = useState({
    length: false,
  });

  const unitSystem = inputs.unit_system;

  const csaValues = [
    result.product_max_length,
    result.horizontal_clearance_without_product,
    result.vertical_clearance_without_product,
  ];

  const handleEditClick = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: true }));
  };

  const handleSaveClick = (field, value, displayedUnit) => {
    const baseValue = ToBaseUnit(value, displayedUnit);
    handleInputChange(
      'product_max_length',
      baseValue,
      inputs,
      setInputs,
      setOptions
    );
    setEditMode((prev) => ({ ...prev, [field]: false }));
  };

  return (
    <div className="result-product-container">
      {['Length', 'Width', 'Height'].map((label, i) => {
        const rawValue = csaValues[i];
        const { displayedValue, displayedUnit } = UnitConversion(
          rawValue,
          unitSystem,
          'mm'
        );
        const value =
          typeof displayedValue === 'number'
            ? Math.round(displayedValue * 10) / 10
            : '';
        const isChecked = typeof rawValue === 'number' && rawValue > 0;

        return (
          <div
            key={i}
            className={`result-product ${!isChecked && 'not-checked'}`}
          >
            <div className="result-product-name">{label}</div>
            {label === 'Length' &&
            editMode.length &&
            inputs.sv_family !== 'SV-L' ? (
              <input
                key={`length-input-${unitSystem}`}
                className="edit-mode"
                type="number"
                defaultValue={value}
                onBlur={(e) =>
                  handleSaveClick('length', e.target.value, displayedUnit)
                }
                autoFocus
              />
            ) : (
              <>
                <div className="result-product-value">
                  {value} {displayedUnit}
                  {label === 'Length' && inputs.sv_family !== 'SV-L' && (
                    <img
                      className="modify-icon"
                      src={`${process.env.PUBLIC_URL}/icons/modify.png`}
                      alt="Modify"
                      onClick={() => handleEditClick('length')}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Result;
